import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Sparkles,
  BookOpen,
  FileText,
  ShieldCheck,
  Eye,
  EyeOff,
  Loader2,
  ArrowRight,
} from "lucide-react";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const trpcUtils = trpc.useUtils();

  const loginMutation = trpc.auth.login.useMutation({
    onSuccess: async () => {
      toast.success("Logged in successfully");
      await Promise.all([
        trpcUtils.auth.me.invalidate(),
        trpcUtils.profile.getMyProfile.invalidate(),
      ]);
      navigate("/");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to login");
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    loginMutation.mutate({ email, password });
  };

  return (
    <div className="flex min-h-screen w-full bg-background">
      {/* ── Left Branded Showcase Panel (Desktop) ── */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-muted/40 border-r border-border flex-col justify-between p-12 overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none translate-x-1/3 translate-y-1/3" />

        {/* Brand */}
        <div className="relative z-10 flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg shadow-sm">
            P
          </div>
          <div>
            <span className="font-bold tracking-tight text-lg text-foreground block">Profolio</span>
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
              Academic Platform
            </span>
          </div>
        </div>

        {/* Core Value Proposition */}
        <div className="relative z-10 space-y-6 max-w-md">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold border border-primary/20">
            <Sparkles className="h-3.5 w-3.5" />
            <span>Built specifically for scholars & researchers</span>
          </div>

          <h2 className="text-3xl font-extrabold tracking-tight text-foreground leading-tight">
            Elevate your academic presence with clarity and elegance.
          </h2>

          <div className="space-y-4 pt-2">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-lg bg-background border border-border flex items-center justify-center text-primary shrink-0 mt-0.5">
                <BookOpen className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-foreground">Curated Academic Layouts</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Pre-configured for publications, research narratives, teaching dossiers, and lab teams.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-lg bg-background border border-border flex items-center justify-center text-primary shrink-0 mt-0.5">
                <FileText className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-foreground">Instant PDF CV Generation</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Always-in-sync curriculum vitae with high-resolution export and live shareable links.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-lg bg-background border border-border flex items-center justify-center text-primary shrink-0 mt-0.5">
                <ShieldCheck className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-foreground">Direct Cloudflare Edge CDN</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Sub-50ms global load times with instant cache invalidation upon every publication.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer info */}
        <div className="relative z-10 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Profolio Inc. All rights reserved.
        </div>
      </div>

      {/* ── Right Form Panel ── */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm space-y-6">
          {/* Mobile brand header */}
          <div className="flex lg:hidden items-center gap-2 mb-2">
            <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm">
              P
            </div>
            <span className="font-bold text-base text-foreground">Profolio</span>
          </div>

          <div>
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Welcome back</h1>
            <p className="text-xs text-muted-foreground mt-1">
              Sign in to manage your academic portfolio and publications.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">Institutional / Personal Email</label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="scholar@university.edu"
                className="h-10 text-xs"
              />
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-foreground">Password</label>
              </div>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-10 text-xs pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loginMutation.isPending}
              className="w-full h-10 text-xs font-semibold gap-1.5 mt-2"
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </>
              )}
            </Button>
          </form>

          <p className="text-center text-xs text-muted-foreground">
            Don't have an academic profile yet?{" "}
            <Link to="/register" className="font-semibold text-primary hover:underline">
              Create account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
